import { useRef, useState } from 'react';
import useFetchJobChangeProcesses from '../../job_change_processes/api/useFetchJobChangeProcesses';
import JobChangeProcessListItem from '../../job_change_processes/components/list/JobChangeProcessListItem';
import JobChangeProcessStatusUpdateModal from '../../job_change_processes/components/modal/JobChangeProcessStatusUpdateModal';
import ListItemLoading from '../../job_change_processes/components/list/ListItemLoading';
import ListItemNoData from '../../job_change_processes/components/list/ListItemNoData';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import useRollbackHandler from '../../job_change_processes/hooks/useRollbackHandler';
import JobChangeProcessSidebar from '../../job_change_processes/components/sidebar/JobChangeProcessDetailSidebar';
import JobChangeProcessLogStatusUpdateModal from '../../job_change_processes/components/modal/JobChangeProcessLogStatusUpdateModal';
import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';

type Props = {
  jobPostId: number;
};

export default function ProvidedJobPostsMatchingCard({ jobPostId }: Props) {
  const queryClient = useQueryClient();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { data, refetch, isLoading } = useFetchJobChangeProcesses({
    provided_job_post_id: jobPostId.toString(),
    result: ['processing', 'rejected', 'declined', 'offered', 'closed'],
  });
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [logStatusModalOpen, setLogStatusModalOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(sidebarRef, () => {
    setIsSidebarOpen(false);
    setIsVisible(false);
  });

  const openSidebar = (isValue: boolean) => {
    setIsSidebarOpen(isValue);
    setIsVisible(isValue);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setTimeout(() => setIsVisible(false), 50);
  };

  const scrollToTarget = () => {
    listRef?.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const updateJobchangeProcesses = async () => {
    await refetch();
    // 求人情報のカウンターも更新
    queryClient.invalidateQueries({ queryKey: ['providedJobPost', jobPostId] });
    scrollToTarget();
  };

  const { handleRollback, isRollbackPending } = useRollbackHandler(async () => {
    closeSidebar();
    await updateJobchangeProcesses();
  });

  return (
    <>
      <div className="relative h-full" ref={listRef}>
        <div className="flex h-full flex-col">
          <div className="sticky top-[60px] z-10 bg-gray">
            <div className="flex h-[36px] min-h-[36px] items-center justify-around rounded-s bg-tag-gray px-6">
              <div className="grid w-full grid-cols-[2fr_1fr_1fr_1fr] gap-4">
                <p className="text-xs font-bold text-sub">求職者</p>
                <p className="text-center text-xs font-bold text-sub">ステータス</p>
                <p className="text-center text-xs font-bold text-sub">選考日程</p>
                <p className="text-center text-xs font-bold text-sub">メモ</p>
              </div>
            </div>
          </div>
          <div className="hidden-scrollbar mt-4 flex flex-1 flex-col gap-3 overflow-y-auto p-1">
            {isLoading ? (
              <>
                {Array.from({ length: 10 }).map((_, key) => {
                  return <ListItemLoading key={key} />;
                })}
              </>
            ) : (
              <>
                {data?.records.length === 0 ? (
                  <div className="mt-[100px]">
                    <ListItemNoData />
                  </div>
                ) : (
                  data?.records.map((j, key) => {
                    return (
                      <JobChangeProcessListItem
                        key={key}
                        jobChangeProcess={j}
                        openSidebar={openSidebar}
                        statusModalOpen={() => setStatusModalOpen(true)}
                        jobchangeProcessesRefetch={updateJobchangeProcesses}
                        isSidebarOpen={isSidebarOpen}
                      />
                    );
                  })
                )}
              </>
            )}
          </div>
          <JobChangeProcessStatusUpdateModal
            open={statusModalOpen}
            onOpenChange={setStatusModalOpen}
            jobchangeProcessesRefetch={updateJobchangeProcesses}
          />
        </div>
      </div>
      <div>
        {isVisible && (
          <div
            ref={sidebarRef}
            className={clsx(
              'fixed top-0 right-0 z-50 h-screen w-[415px] overflow-hidden bg-white shadow-[0px_0px_6px_0px_#00000033]',
              isSidebarOpen ? 'animate-slideInRight' : 'animate-fadeOutRight'
            )}
          >
            <div className="flex h-full flex-col overflow-hidden p-4">
              <JobChangeProcessSidebar
                closeSidebar={closeSidebar}
                statusOpen={() => setLogStatusModalOpen(true)}
                isSidebarOpen={isSidebarOpen}
                onRollback={handleRollback}
                isRollbackPending={isRollbackPending}
              />
            </div>
          </div>
        )}
        <JobChangeProcessLogStatusUpdateModal
          open={logStatusModalOpen}
          onOpenChange={setLogStatusModalOpen}
          jobchangeProcessesRefetch={updateJobchangeProcesses}
        />
      </div>
    </>
  );
}
